import { useState, useEffect } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  SelectInput,
  ReorderButtons,
} from "../../../components/FormField";

import { moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

const emptyFaq = () => ({ question: "", answer: "" });
const emptyCategory = () => ({ name: "", faqs: [emptyFaq()] });

// ─── Collapsible FAQ Category Block ──────────────────────────────────────────
function CategoryBlock({ cat, idx, total, onChange, onMove, onRemove }) {
  const [open, setOpen] = useState(true);

  const updCat = (key, val) => onChange({ ...cat, [key]: val });
  const updFaq = (fi, key, val) =>
    onChange({
      ...cat,
      faqs: cat.faqs.map((f, i) => (i === fi ? { ...f, [key]: val } : f)),
    });
  const addFaq = () =>
    onChange({ ...cat, faqs: [...(cat.faqs || []), emptyFaq()] });
  const removeFaq = (fi) =>
    onChange({ ...cat, faqs: cat.faqs.filter((_, i) => i !== fi) });
  const moveFaq = (f, t) =>
    onChange({ ...cat, faqs: moveItem(cat.faqs, f, t) });

  return (
    <div className="border rounded-xl mb-4 overflow-hidden">
      {/* Category header */}
      <div className="flex items-center justify-between px-4 py-3 bg-muted border-b border-border">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <p className="text-xs font-semibold">
            {cat.name || `Category ${idx + 1}`}
            <span className="ml-2 text-muted-foreground font-normal">
              ({cat.faqs?.length || 0} Q&amp;A)
            </span>
          </p>
        </button>
        <div className="flex gap-2 items-center">
          <ReorderButtons index={idx} total={total} onMove={onMove} />
          {total > 1 && (
            <button onClick={onRemove}>
              <Trash2 size={14} className="text-destructive" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="p-4 space-y-4">
          <Field label="Category Name">
            <TextInput
              value={cat.name}
              placeholder="e.g. General, Pricing, Support"
              onChange={(e) => updCat("name", e.target.value)}
            />
          </Field>

          {/* FAQs inside category */}
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs font-semibold">Q&amp;A Pairs</p>
            <button
              onClick={addFaq}
              className="flex items-center gap-1 text-xs"
            >
              <Plus size={12} /> Add Q&amp;A
            </button>
          </div>

          {cat.faqs?.map((faq, fi) => (
            <div
              key={fi}
              className="border border-border/60 rounded-lg p-3 space-y-3 bg-background"
            >
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground font-medium">
                  Q&amp;A {fi + 1}
                </p>
                <div className="flex gap-2">
                  <ReorderButtons
                    index={fi}
                    total={cat.faqs.length}
                    onMove={(f, t) => moveFaq(f, t)}
                  />
                  {cat.faqs.length > 1 && (
                    <button onClick={() => removeFaq(fi)}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
              <Field label="Question">
                <TextInput
                  value={faq.question}
                  placeholder="Enter question..."
                  onChange={(e) => updFaq(fi, "question", e.target.value)}
                />
              </Field>
              <Field label="Answer">
                <TextArea
                  value={faq.answer}
                  placeholder="Enter answer..."
                  onChange={(e) => updFaq(fi, "answer", e.target.value)}
                />
              </Field>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function FAQSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeIcon, setHeroBadgeIcon] = useState("HelpCircle");
  const [heroBadgeText, setHeroBadgeText] = useState("Help Center");
  const [heroHeading, setHeroHeading] = useState("Frequently Asked");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Questions");
  const [heroDescription, setHeroDescription] = useState("");

  /* ---------------- FAQ CATEGORIES ---------------- */
  const [faqActive, setFaqActive] = useState(true);
  const [categories, setCategories] = useState([emptyCategory()]);

  /* ---------------- STILL HAVE QUESTIONS ---------------- */
  const [contactActive, setContactActive] = useState(true);
  const [contactIcon, setContactIcon] = useState("MessageCircle");
  const [contactHeading, setContactHeading] = useState("Still Have Questions?");
  const [contactDescription, setContactDescription] = useState("");
  const [contactCtaText, setContactCtaText] = useState("Contact Us");
  const [contactCtaLink, setContactCtaLink] = useState("/contact");
  const [contactPhone, setContactPhone] = useState("+61 3 7075 3900");

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeIcon(d.hero.badge_icon || "HelpCircle");
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
    }

    if (d.faq_categories?.length) setCategories(d.faq_categories);

    if (d.still_have_questions) {
      setContactIcon(d.still_have_questions.icon || "MessageCircle");
      setContactHeading(d.still_have_questions.heading || "");
      setContactDescription(d.still_have_questions.description || "");
      setContactCtaText(d.still_have_questions.cta_text || "");
      setContactCtaLink(d.still_have_questions.cta_link || "");
      setContactPhone(d.still_have_questions.phone || "");
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_icon: heroBadgeIcon,
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
    },
    faq_categories: categories,
    still_have_questions: {
      icon: contactIcon,
      heading: contactHeading,
      description: contactDescription,
      cta_text: contactCtaText,
      cta_link: contactCtaLink,
      phone: contactPhone,
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeIcon,
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    categories,
    contactIcon,
    contactHeading,
    contactDescription,
    contactCtaText,
    contactCtaLink,
    contactPhone,
  ]);

  const updateCategory = (i, val) =>
    setCategories((s) => s.map((x, idx) => (idx === i ? val : x)));

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Badge Icon">
            <SelectInput
              value={heroBadgeIcon}
              options={ICON_OPTIONS}
              onChange={(e) => setHeroBadgeIcon(e.target.value)}
            />
          </Field>
          <Field label="Badge Text">
            <TextInput
              value={heroBadgeText}
              onChange={(e) => setHeroBadgeText(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* FAQ CATEGORIES */}
      <CollapsibleSection
        title="FAQ Categories & Questions"
        active={faqActive}
        onToggleActive={setFaqActive}
      >
        <p className="text-xs text-muted-foreground mb-4">
          Categories appear as filter buttons on the page. Each category
          contains its own set of Q&amp;A pairs.
        </p>

        {categories.map((cat, i) => (
          <CategoryBlock
            key={i}
            cat={cat}
            idx={i}
            total={categories.length}
            onChange={(val) => updateCategory(i, val)}
            onMove={(f, t) => setCategories(moveItem(categories, f, t))}
            onRemove={() =>
              setCategories((s) => s.filter((_, idx) => idx !== i))
            }
          />
        ))}

        <button
          onClick={() => setCategories((s) => [...s, emptyCategory()])}
          className="w-full flex items-center justify-center gap-2 text-xs py-3 border-2 border-dashed border-border rounded-xl hover:border-accent/40 hover:text-accent transition-colors"
        >
          <Plus size={14} /> Add Category
        </button>
      </CollapsibleSection>

      {/* STILL HAVE QUESTIONS */}
      <CollapsibleSection
        title="Still Have Questions"
        active={contactActive}
        onToggleActive={setContactActive}
      >
        <Field label="Icon">
          <SelectInput
            value={contactIcon}
            options={ICON_OPTIONS}
            onChange={(e) => setContactIcon(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={contactHeading}
            onChange={(e) => setContactHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={contactDescription}
            onChange={(e) => setContactDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={contactCtaText}
              onChange={(e) => setContactCtaText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={contactCtaLink}
              onChange={(e) => setContactCtaLink(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Phone Number">
          <TextInput
            value={contactPhone}
            placeholder="+61 3 7075 3900"
            onChange={(e) => setContactPhone(e.target.value)}
          />
        </Field>
      </CollapsibleSection>
    </>
  );
}
